import React, { useContext, useState } from "react";
import { useSelector } from "react-redux";
import { State } from "../../redux/store";
import { DoctorInterface, ShowDoctorContext } from "./types";
import { hostName } from "../../requestMethods";
import { formatDate, formattedDate } from "../../functions/functions";
import { FaCheck, FaEdit, FaEyeSlash } from "react-icons/fa";
import { MdRemoveCircle } from "react-icons/md";
import { useNavigate } from "react-router";
import { InputElement } from "../../HtmlComponents/InputElement";
import { InputRadioCustom } from "../../HtmlComponents/InputRadioCustom";

const DataDoctors: React.FC = () => {
  const { doctors } = useSelector((state: State) => state.doctors);
  const { medecinTypes } = useSelector((state: State) => state.medecinType);

  const {
    showEditModal,
    setShowEditModal,
    showDeleteModal,
    setShowDeleteModal,
    setSelectedDoctor,
    showDisappearModal,
    setShowDisappearModal,
  } = useContext(ShowDoctorContext);

  const toggleEditDoctor = (doctor: DoctorInterface) => {
    setSelectedDoctor(doctor);
    setShowEditModal(!showEditModal);
  };

  const toggleDeleteDoctor = (doctor: DoctorInterface) => {
    setSelectedDoctor(doctor);
    setShowDeleteModal(!showDeleteModal);
  };

  const toggleDisappearDoctor = (doctor: DoctorInterface) => {
    setSelectedDoctor(doctor);
    setShowDisappearModal(!showDisappearModal);
  };

  const navigate = useNavigate();
  const [dataType, setDataType] = useState("table");
  const [typeMedecin, setTypeMedecin] = useState(medecinTypes[1]?.title);
  const [search, setSearch] = useState("");
  const [suspended, setSuspended] = useState(false);

  // Filtrer les docteurs selon les critères sélectionnés
  const filteredDoctors = doctors
    ?.filter((doctor: DoctorInterface) =>
      doctor.medecinType.title.includes(typeMedecin)
    )
    ?.filter((doctor: DoctorInterface) => doctor.status.disappeared === suspended)
    ?.filter(
      (doctor: DoctorInterface) =>
        doctor.fullName?.toUpperCase().includes(search.toUpperCase()) ||
        doctor.RegNo.toString().includes(search)
    )
    ?.sort((a: DoctorInterface, b: DoctorInterface) =>
      Number(a.RegNo ?? 0) - Number(b.RegNo ?? 0)
    );

  return (
    <>
      {/* Filtres */}
      <section className="grid md:grid-cols-3 gap-4 mb-4">
        <div className="col-span-2 space-y-4">
          <div className="flex items-center gap-4 flex-wrap">
            <p className="text-lg font-semibold whitespace-nowrap">Afficher par :</p>
            <InputRadioCustom
              labelName="Table"
              id="Table"
              name="datatype"
              checked={dataType === "table"}
              value={"table"}
              setValue={setDataType}
            />
            <InputRadioCustom
              labelName="Carte"
              id="Carte"
              name="datatype"
              checked={dataType === "carte"}
              value={"carte"}
              setValue={setDataType}
            />
          </div>

          <div className="flex items-center gap-4 flex-wrap">
            <p className="text-lg font-semibold whitespace-nowrap">Type de médecin :</p>
            {medecinTypes.map((medecin: any) => (
              <InputRadioCustom
                key={medecin._id}
                labelName={medecin.title}
                id={medecin.title}
                name="medecintype"
                checked={typeMedecin === medecin.title}
                value={medecin.title}
                setValue={setTypeMedecin}
              />
            ))}
          </div>

          <div className="flex items-center gap-4 flex-wrap">
            <p className="text-lg font-semibold whitespace-nowrap">Suspendu :</p>
            <InputRadioCustom
              labelName="Oui"
              id="oui"
              name="suspended"
              checked={suspended === true}
              boolean={true}
              booleanValue={true}
              setValue={setSuspended} value={""}            />
            <InputRadioCustom
              labelName="Non"
              id="non"
              name="suspended"
              checked={suspended === false}
              boolean={true}
              booleanValue={false}
              setValue={setSuspended} value={""}            />
          </div>
        </div>

        {/* Total */}
        <div className="text-3xl font-bold flex items-center justify-center md:justify-end">
          Total : <span className="ml-2 text-main">{filteredDoctors?.length}</span>
        </div>
      </section>

      {/* Recherche */}
      <div className="mb-4">
        <InputElement
          name="Chercher un docteur"
          placeholder="Chercher un docteur par son nom ou matricule"
          value={search}
          setValue={setSearch}
        />
      </div>

      {/* Affichage Table ou Carte */}
      {dataType === "table" ? (
        <div className="overflow-x-auto rounded-md shadow-md border border-gray-300">
          <table className="min-w-full divide-y divide-gray-200 text-sm">
            <thead className="bg-main text-white sticky top-0 z-10">
              <tr>
                {[
                  "Numéro d'inscription",
                  "Nom",
                  "Sexe",
                  "Âge",
                  "Lieu de naissance",
                  "Date d'inscription",
                  "Téléphone",
                  "WhatsApp",
                  "Email",
                  "Propriétaire",
                  "Spécialité",
                  "Actions",
                ].map((title) => (
                  <th
                    key={title}
                    className="px-4 py-3 text-left font-semibold border-r border-main whitespace-nowrap"
                  >
                    {title}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredDoctors?.map((doctor: DoctorInterface, index: number) => (
                <tr
                  key={doctor._id}
                  className={`hover:bg-blue-50 cursor-pointer ${
                    index % 2 === 0 ? "bg-white" : "bg-gray-50"
                  }`}
                >
                  <td className="px-4 py-2 border-r border-blue-300 font-medium">
                    {doctor.RegNo}/{doctor.medecinType?.title ?? ""}
                  </td>
                  <td
                    className="px-4 py-2 border-r border-blue-300 font-medium text-main underline"
                    onClick={() => navigate(`${doctor._id}/about/regform`)}
                  >
                    {doctor.fullName}
                  </td>
                  <td className="px-4 py-2 border-r border-blue-300">{doctor.sex}</td>
                  <td className="px-4 py-2 border-r border-blue-300">
                    {new Date().getFullYear() - Number(doctor.yearOfBirth)}
                  </td>
                  <td className="px-4 py-2 border-r border-blue-300">{doctor.placeOfBirth}</td>
                  <td className="px-4 py-2 border-r border-blue-300">
                    {formattedDate(doctor.inscriptionDate)}
                  </td>
                  <td className="px-4 py-2 border-r border-blue-300">{doctor.contact?.phone}</td>
                  <td className="px-4 py-2 border-r border-blue-300">{doctor.contact?.whatsapp}</td>
                  <td className="px-4 py-2 border-r border-blue-300">{doctor.contact?.email}</td>
                  <td className="px-4 py-2 border-r border-blue-300 text-center">
                    {doctor.isOwner && (
                      <FaCheck className="text-green-500 mx-auto text-xl" />
                    )}
                  </td>
                  <td className="px-4 py-2 border-r border-blue-300">{doctor.speciality}</td>
                  <td className="px-4 py-2 border-blue-300">
                    <div className="flex justify-center items-center gap-4">
                      <FaEyeSlash
                        title="Toggle disparition"
                        className={`cursor-pointer ${
                          doctor.status?.disappeared
                            ? "text-gray-400"
                            : "text-red-600 hover:text-red-800"
                        } text-xl`}
                        onClick={() => toggleDisappearDoctor(doctor)}
                      />
                      <FaEdit
                        title="Éditer"
                        className="cursor-pointer text-blue-600 hover:text-blue-800 text-xl"
                        onClick={() => toggleEditDoctor(doctor)}
                      />
                      <MdRemoveCircle
                        title="Supprimer"
                        className="cursor-pointer text-red-600 hover:text-red-800 text-xl"
                        onClick={() => toggleDeleteDoctor(doctor)}
                      />
                    </div>
                  </td>
                </tr>
              ))}
              {filteredDoctors?.length === 0 && (
                <tr>
                  <td
                    colSpan={12}
                    className="text-center text-gray-500 py-6 font-semibold"
                  >
                    Aucun médecin trouvé.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="grid lg:grid-cols-3 md:grid-cols-2 gap-6">
          {filteredDoctors?.map((doctor: DoctorInterface) => (
            <div
              key={doctor._id}
              className="bg-white rounded-lg shadow-lg p-6 relative flex flex-col items-center"
            >
              {/* Icone formulaire en haut droite */}
              <img
                onClick={() => navigate(`${doctor._id}/about/regform`)}
                src="assets/images/about.png"
                alt="Formulaire d'inscription"
                className="w-14 h-14 rounded-full shadow-md absolute top-3 right-3 cursor-pointer hover:scale-105 transition-transform"
              />

              {/* Avatar */}
              {doctor.avatar ? (
                <img
                  src={hostName + "imgs/doctors/" + doctor.avatar}
                  alt={`${doctor.fullName}-profile`}
                  className="w-40 h-40 rounded-full shadow-md object-cover"
                />
              ) : (
                <div className="w-40 h-40 rounded-full shadow-md bg-gray-100 flex items-center justify-center text-gray-400 text-4xl font-bold">
                  {doctor.fullName.charAt(0)}
                </div>
              )}

              {/* Infos */}
              <div className="mt-6 w-full space-y-2 text-gray-700 font-medium">
                <p className="flex justify-between">
                  <span>Numéro d'inscription :</span>{" "}
                  <span>{doctor.RegNo}/{doctor.medecinType?.title ?? ""}</span>
                </p>
                <p className="flex justify-between">
                  <span>Nom :</span> <span>{doctor.fullName}</span>
                </p>
                <p className="flex justify-between">
                  <span>Sexe :</span> <span>{doctor.sex}</span>
                </p>
                <p className="flex justify-between">
                  <span>Âge :</span>{" "}
                  <span>{new Date().getFullYear() - Number(doctor.yearOfBirth)}</span>
                </p>
                <p className="flex justify-between">
                  <span>Lieu de naissance :</span> <span>{doctor.placeOfBirth}</span>
                </p>
                <p className="flex justify-between">
                  <span>Date d'inscription :</span>{" "}
                  <span>{formatDate(doctor.inscriptionDate)}</span>
                </p>
                <p className="flex justify-between">
                  <span>Téléphone :</span> <span>{doctor.contact?.phone}</span>
                </p>
                <p className="flex justify-between">
                  <span>WhatsApp :</span> <span>{doctor.contact?.whatsapp}</span>
                </p>
                <p className="flex justify-between">
                  <span>Email :</span> <span>{doctor.contact?.email}</span>
                </p>
                {doctor.cabinet?.name && (
                  <p className="flex justify-between">
                    <span>Cabinet :</span> <span>{doctor.cabinet.name}</span>
                  </p>
                )}
                {doctor.isOwner && (
                  <p className="flex justify-between items-center gap-1 text-green-600">
                    <span>Propriétaire :</span> <FaCheck className="text-xl" />
                  </p>
                )}
                <p className="flex justify-between">
                  <span>Spécialité :</span> <span>{doctor.speciality}</span>
                </p>
              </div>

              {/* Actions */}
              <div className="mt-4 flex justify-center gap-6 text-xl w-full text-center">
                <FaEyeSlash
                  title="Toggle disparition"
                  className={`cursor-pointer ${
                    doctor.status?.disappeared
                      ? "text-gray-400"
                      : "text-red-600 hover:text-red-800"
                  }`}
                  onClick={() => toggleDisappearDoctor(doctor)}
                />
                <FaEdit
                  title="Éditer"
                  className="cursor-pointer text-blue-600 hover:text-blue-800"
                  onClick={() => toggleEditDoctor(doctor)}
                />
                <MdRemoveCircle
                  title="Supprimer"
                  className="cursor-pointer text-red-600 hover:text-red-800"
                  onClick={() => toggleDeleteDoctor(doctor)}
                />
              </div>
            </div>
          ))}
          {filteredDoctors?.length === 0 && (
            <p className="text-center text-gray-500 font-semibold col-span-full">
              Aucun médecin trouvé.
            </p>
          )}
        </div>
      )}
    </>
  );
};

export default DataDoctors;
